import { makeStyles } from "@mui/styles";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import { IconCollection } from "components/Icon/IconCollection";
import IconButton from "components/IconButton";
import { Popover } from "components/Popover/Popover";
import { ReactNode } from "react";
import "./CommentFullscreenModal.scss";

interface CommentFullscreenModalProps {
    isVisible: boolean;
    onCancel: () => void;
    body: string;
    header?: ReactNode;
}

/**
 * Near-fullscreen reading overlay for a single comment
 * (operator-task-fe-comment-fullscreen-expand, Margot). Mirrors the code
 * editor's fullscreen affordance that Margot cited as the reference: it reuses
 * the shared `Popover` shell the `VisualCodeEditorModal` is built on (a
 * `document.body` portal + backdrop, viewport-centered via
 * `anchorReference="none"`), so the comment overlay matches the established
 * fullscreen pattern rather than inventing a new one.
 *
 * The comment is rendered with the SAME `Markdown` component as the inline view,
 * just unconstrained — a comfortable reading width with its own scroll — so long
 * comments are readable without the limited inline scroll window. Dismissed via
 * the top-right collapse control, a backdrop click, or Escape.
 */
export const CommentFullscreenModal = ({
    isVisible,
    onCancel,
    body,
    header,
}: CommentFullscreenModalProps) => {
    const useStyles = makeStyles(() => ({
        rootStyle: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
        },
        // A comfortable reading width rather than the code editor's full width —
        // long prose is hard to read edge-to-edge (ui-design-principle-
        // typography). Capped to the viewport on small screens; its own scroll
        // when the content is taller than the overlay.
        paperStyle: {
            width: "min(90vw, 900px)",
            maxHeight: "90vh",
            overflow: "auto",
        },
    }));
    const classes = useStyles();

    return (
        <Popover
            isOpen={isVisible}
            anchorReference="none"
            rootClassName="commentFullscreenModal"
            classes={{ root: classes.rootStyle, paper: classes.paperStyle }}
            closeOnInsideClick={false}
            // Disable the Popover's document-level outside-click handler: it races
            // the very click that OPENS this overlay. The expand button sits
            // OUTSIDE the popover, and React flushes its onClick (which opens the
            // overlay) before the document bubble listener runs — so that same
            // opening click is then read as an "outside click" and would
            // immediately re-close the overlay (net: it never appears). Backdrop
            // clicks and Escape still close the overlay, routed through MUI's own
            // onClose -> onBlur (both target the backdrop/modal itself, so they do
            // not race the open). The reference VisualCodeEditorModal avoids the
            // same race by passing no closeHandler at all.
            closeOnOutsideClick={false}
            onBlur={onCancel}
        >
            <div className="commentFullscreenModalContent">
                {/*
                 * Top-right collapse control, mirroring the code editor's
                 * fullscreen collapse (VisualCodeEditorModal). An always-visible
                 * control inside the overlay (not a hover-reveal) — it is the
                 * primary way back, so it must be obvious.
                 */}
                <IconButton icon={IconCollection.arrowCollapse} onClick={onCancel} />
                {header && <div className="commentFullscreenModalHeader">{header}</div>}
                <Markdown
                    markdownString={body}
                    canCopy
                    className="commentFullscreenModalBody"
                />
            </div>
        </Popover>
    );
};
